import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AxiosError } from 'axios';
import { User, Notification, Case } from '../types';
import { userAuthApi } from '../lib/userAuthApi';
import { setAuthToken, clearAuthToken, getAuthToken } from '../lib/api';
import { decodeAuthToken, isTokenExpired } from '../lib/authToken';

/**
 * Result of a login attempt. `ok` is the success flag; on failure `status`
 * carries the backend HTTP status (401 = bad credentials, 403 = pending
 * approval, 429 = rate-limited, undefined = network/unknown) and `message`
 * the server-provided detail when present. The page uses these to show the
 * right inline message instead of a single generic error.
 */
export interface LoginResult {
  ok: boolean;
  status?: number;
  message?: string;
}

interface AppState {
  // Authentication state
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  logout: () => void;
  initializeAuth: () => Promise<void>;

  // User state
  currentUser: User | null;
  setCurrentUser: (user: User | null) => void;
  setAuthenticated: (authenticated: boolean) => void;

  // Cases state
  activeCase: Case | null;
  setActiveCase: (project: Case | null) => void;
  cases: Case[];
  setCases: (cases: Case[]) => void;
  addCase: (caseData: Case) => void;
  updateCase: (id: string, updates: Partial<Case>) => void;
  removeCase: (id: string) => void;

  // UI state
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;

  // Theme state
  theme: 'light' | 'dark';
  setTheme: (theme: 'light' | 'dark') => void;

  // Notifications state
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'id'>) => void;
  markNotificationRead: (id: string) => void;
  clearNotifications: () => void;
}

// Mirror the authenticated user's email into the plain localStorage keys that
// non-store readers depend on for the `X-User-Email` header:
//   - lib/api/ontologySuggestionApi.ts + lib/api/agentBuilderApi.ts read 'user_email'
//   - pages/analysis/Step4SchemaPreviewEnhanced.tsx reads 'currentUser'
// Without this, those readers fall back to '' / 'unknown@example.com', which fails
// the backend case-access check (403) even when the real user HAS access. The auth
// store is the single writer; readers stay decoupled (no lib/api <-> stores cycle).
const persistUserIdentity = (user: User | null) => {
  try {
    if (user?.email) {
      localStorage.setItem('user_email', user.email);
      localStorage.setItem('currentUser', JSON.stringify(user));
    } else {
      localStorage.removeItem('user_email');
      localStorage.removeItem('currentUser');
    }
  } catch {
    /* localStorage may be unavailable (private mode / SSR) — non-fatal */
  }
};

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      // Authentication state
      isAuthenticated: false,
      isLoading: false,
      login: async (email: string, password: string) => {
        try {
          set({ isLoading: true });
          // Call real backend API
          const response = await userAuthApi.login({ email, password });

          // Store token
          setAuthToken(response.token);

          // Transform backend user to frontend User type
          const user: User = {
            id: response.user.id,
            name: `${response.user.first_name} ${response.user.last_name}`,
            email: response.user.email,
            role: response.user.role,
          };

          persistUserIdentity(user);
          set({ isAuthenticated: true, currentUser: user, isLoading: false });
          return { ok: true };
        } catch (error) {
          console.error('Login failed:', error);
          set({ isLoading: false });

          const axiosErr = error as AxiosError<{ detail?: string }>;
          const status = axiosErr.response?.status;
          const detail =
            typeof axiosErr.response?.data?.detail === 'string'
              ? axiosErr.response.data.detail
              : undefined;
          return { ok: false, status, message: detail };
        }
      },
      logout: () => {
        clearAuthToken();
        persistUserIdentity(null);
        set({ isAuthenticated: false, currentUser: null });
      },
      initializeAuth: async () => {
        const token = getAuthToken();
        if (!token) {
          // No token — nothing to validate. Clear any stale persisted session so
          // the UI can't show an authenticated (possibly Admin) state with no
          // credential behind it.
          persistUserIdentity(null);
          set({ isAuthenticated: false, currentUser: null });
          return;
        }

        // A token exists — restore the session from it. This backend exposes no
        // /auth/me, so there is nothing to validate against: we decode the token
        // we already hold. Only a genuinely expired `exp` logs the user out; a
        // network blip or a missing endpoint must never do so, which is exactly
        // what used to happen — every refresh called /auth/me, got a 404, and
        // fell into the logout branch below.
        const claims = decodeAuthToken(token);
        if (isTokenExpired(claims)) {
          clearAuthToken();
          persistUserIdentity(null);
          set({ isAuthenticated: false, currentUser: null });
          return;
        }

        // Prefer the token's own claims (the backend signed them, so they are
        // fresher than anything we persisted) and fall back to the persisted
        // user for fields the JWT does not carry.
        const persisted = get().currentUser;
        const fullName =
          claims?.name ||
          [claims?.first_name, claims?.last_name].filter(Boolean).join(' ').trim();

        const user: User = {
          id: claims?.user_id || claims?.sub || persisted?.id || '',
          name: fullName || persisted?.name || '',
          email: claims?.email || persisted?.email || '',
          role: (claims?.role as User['role']) || persisted?.role || 'Pending',
        };

        persistUserIdentity(user);
        set({ isAuthenticated: true, currentUser: user });
      },

      // User state
      currentUser: null,
      setCurrentUser: (user) => {
        // Funnel used by Microsoft SSO (Login.tsx) and any programmatic update —
        // keep the identity keys in sync here too.
        persistUserIdentity(user);
        set({ currentUser: user });
      },
      setAuthenticated: (authenticated) => set({ isAuthenticated: authenticated }),

      // Cases state
      activeCase: null,
      setActiveCase: (caseItem) => set({ activeCase: caseItem }),
      cases: [],
      setCases: (cases) => set({ cases }),
      addCase: (caseItem) =>
        set((state) => ({ cases: [caseItem, ...state.cases] })),
      updateCase: (id, updates) =>
        set((state) => ({
          cases: state.cases.map((p) =>
            p.id === id ? { ...p, ...updates } : p
          ),
          activeCase:
            state.activeCase?.id === id
              ? { ...state.activeCase, ...updates }
              : state.activeCase,
        })),
      removeCase: (id) =>
        set((state) => ({
          cases: state.cases.filter((p) => p.id !== id),
          activeCase: state.activeCase?.id === id ? null : state.activeCase,
        })),

      // UI state
      sidebarCollapsed: false,
      toggleSidebar: () =>
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),

      // Theme state
      theme: 'light',
      setTheme: (theme) => set({ theme }),

      // Notifications state
      notifications: [],
      addNotification: (notification) =>
        set((state) => ({
          notifications: [
            {
              ...notification,
              id: `notif-${Date.now()}`,
            },
            ...state.notifications,
          ],
        })),
      markNotificationRead: (id) =>
        set((state) => ({
          notifications: state.notifications.map((n) =>
            n.id === id ? { ...n, read: true } : n
          ),
        })),
      clearNotifications: () => set({ notifications: [] }),
    }),
    {
      name: 'app-storage', // name of the item in localStorage
      partialize: (state) => ({
        // Only persist auth-related state
        isAuthenticated: state.isAuthenticated,
        currentUser: state.currentUser,
      }),
    }
  )
);
